import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BLOG_TOPICS } from "../lib/constants";

const FILTERS = ["All", ...BLOG_TOPICS];

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/blog-posts", { params: { topic } })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [topic]);

  return (
    <div data-testid="blog-page">
      <section className="grain-overlay bg-[#111110] py-16 sm:py-20">
        <div className="crow-container">
          <p className="font-hand text-2xl text-[#C27803]">notes from the shop journal</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-[#FDFBF7] sm:text-5xl">
            The Crow Chronicles
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#E8DFC8]">
            Decor wisdom, primitive history, seasonal staging and stories of the makers who fill our shelves.
          </p>
        </div>
      </section>

      <section className="crow-container py-12 sm:py-16">
        <div className="flex flex-wrap gap-2" data-testid="blog-topic-filters">
          {FILTERS.map((t) => (
            <button
              key={t}
              data-testid={`blog-topic-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setTopic(t)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                topic === t
                  ? "border-[#8C251C] bg-[#8C251C] text-[#FDFBF7]"
                  : "border-[#D8CDBC] bg-[#F4EFE6] text-[#292725] hover:border-[#C27803] hover:text-[#C27803]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-20 text-center font-hand text-2xl text-[#8A7E70]" data-testid="blog-loading">turning the pages...</p>
        ) : posts.length === 0 ? (
          <p className="py-20 text-center font-serif text-2xl text-[#292725]" data-testid="blog-empty-state">No stories here yet — check back soon.</p>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                data-testid={`blog-card-${post.id}`}
                className="group flex flex-col overflow-hidden rounded-md border border-[#D8CDBC] bg-[#FDFBF7] shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#E8DFC8]">
                  <img src={post.image_url} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C27803]">{post.topic}</span>
                    <span className="font-mono text-[10px] text-[#8A7E70]">{formatDate(post.created_at)}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-xl font-semibold leading-snug text-[#292725] group-hover:text-[#8C251C]">{post.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#8A7E70]">{post.excerpt}</p>
                  <span className="mt-4 font-hand text-lg text-[#8C251C]">read the story →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
